import React, {useState} from 'react';
import {useMutation, useQuery} from '@apollo/client';
import {useSnackbar} from 'notistack';
import {useNavigate} from 'react-router-dom';
import {Delete} from '@mui/icons-material';
import {Box, Button, Typography} from '@mui/material';
import EditorGrid from '../../../components/EditorGrid/EditorGrid';
import gridColumns from './goalsColumns';
import {datagridSorter} from './utils';

import {
  ListGoals,
  ListGoalsVariables,
  ListGoals_goals,
} from '../../../schema-types/ListGoals';
import {useReviewContext} from '../../../contexts/ReviewContext';
import {Goal} from '../../../schema-types/Goal';
import {DeleteGoal, DeleteGoalVariables} from '../../../schema-types/DeleteGoal';
import Loading from '../../../components/Loading/Loading';
import {CHECK_DELETE_GOAL_DETAILS, DELETE_GOAL, LIST_GOALS} from '../goalsQueries';
import ErrorMessage from '../../../components/ErrorMessage/ErrorMessage';
import {
  GlobalPermissionEnum,
  StatusAtReviewEnum,
} from '../../../schema-types/globalTypes';
import {ScrollableModal} from '../../../components/Common/ScrollableModal';
import {useDjangoContext} from '../../../contexts/useAuthContext';
import {hasPermission} from '../../../contexts/FeatureBoundary';
import {
  CheckDeleteGoalDetails,
  CheckDeleteGoalDetailsVariables,
} from '../../../schema-types/CheckDeleteGoalDetails';
import {OpenInCuro} from '../../../utils/getCuroURL';
import validateUUID from '../../../utils/validateUUID';
import {useRequirementsDentistOrBAU} from '../../../hooks/useRequirementsDentistOrBAU';

export const sortGoals = (showCurrent: boolean, goals: ListGoals_goals[]) => {
  let sortedGoals: ListGoals_goals[];
  if (showCurrent) {
    sortedGoals = goals.filter((el) => {
      if (!el?.statusAtReview) {
        return true;
      }
      return (
        el.statusAtReview !== StatusAtReviewEnum.NO_LONGER_REQUIRED &&
        el.statusAtReview !== StatusAtReviewEnum.SATISFIED
      );
    });
  } else {
    sortedGoals = [...goals];
  }

  return sortedGoals.sort(datagridSorter);
};

export type GoalsGridProps = {
  accountId: string;
  readOnly: boolean;
  minHeight?: number;
};

export const GoalsGrid: React.FC<GoalsGridProps> = ({accountId, readOnly, minHeight}) => {
  const {reviewId} = useReviewContext();
  const {djangoUser} = useDjangoContext();
  const {loading, error, data} = useQuery<ListGoals, ListGoalsVariables>(LIST_GOALS, {
    variables: {
      accountId: validateUUID(accountId)!,
      reviewId: validateUUID(reviewId),
    },
  });
  const {enqueueSnackbar} = useSnackbar();
  const [goalToDelete, setGoalToDelete] = useState<Goal | null>(null);
  const goals = data?.goals || [];
  const [showCurrent, setShowCurrent] = useState(true);
  const sortedGoals = sortGoals(showCurrent, goals);
  const navigate = useNavigate();

  if (loading) {
    return <Loading />;
  }

  if (error) {
    return <ErrorMessage error={error} />;
  }

  return (
    <>
      {goalToDelete ? (
        <ScrollableModal open={!!goalToDelete} onClose={() => setGoalToDelete(null)}>
          <DeleteGoalModal onClose={() => setGoalToDelete(null)} goal={goalToDelete} />
        </ScrollableModal>
      ) : null}
      <EditorGrid
        dataGridId="GoalsGrid"
        rows={sortedGoals}
        getRowClassName={(params) => {
          const row = params.row as ListGoals_goals;
          if (row.curoId) {
            return 'submitted';
          }
          if (row.isFailedSubmission) {
            return 'failed';
          }
          return 'unsubmitted';
        }}
        columns={[
          ...gridColumns,
          {
            field: 'delete',
            headerName: ' ',
            flex: 1,
            editable: false,
            renderCell: (params) => {
              const goal = params.row as Goal;
              if (goal.isInCuro) {
                if (
                  !hasPermission(djangoUser, [
                    GlobalPermissionEnum.can_access_admin_features,
                  ])
                ) {
                  return <div />;
                }
              }
              return (
                <Delete
                  data-testid="delete-goal"
                  style={{cursor: 'pointer'}}
                  color="action"
                  onClick={() => setGoalToDelete(goal)}
                />
              );
            },
          },
        ]}
        loading={loading}
        hideFooter
        error={error}
        onRowDoubleClick={(params) => {
          navigate({
            pathname: `../goals-builder`,
            search: `goalid=${params.row.id}`,
          });
        }}
        rowsFiltered={showCurrent}
        filterRowsFunc={(ev) => setShowCurrent(ev.target.checked)}
        rowsFilteredLabel="Show Current"
        rowsFilteredTooltip={`This hides "No longer required" and "Completed" goals.`}
        minHeight={minHeight ?? '600px'}
        sortModel={[{field: 'dateCreated', sort: 'desc'}]}
        createFunc={false}
        createFuncLoading={false}
        syncFunc={() => {}}
        updateFunc={false}
      />
    </>
  );
};

const DeleteGoalModal: React.FC<{
  onClose: () => void;
  goal: Goal;
}> = ({onClose, goal}) => {
  const {enqueueSnackbar} = useSnackbar();
  const {djangoUser} = useDjangoContext();
  const {createRecommendUrl} = useRequirementsDentistOrBAU();
  const {data, loading, refetch} = useQuery<
    CheckDeleteGoalDetails,
    CheckDeleteGoalDetailsVariables
  >(CHECK_DELETE_GOAL_DETAILS, {
    variables: {
      goalId: goal.id,
    },
    pollInterval: 5000,
  });
  const [deleteGoalMutation, {loading: deleteLoading}] = useMutation<
    DeleteGoal,
    DeleteGoalVariables
  >(DELETE_GOAL, {
    refetchQueries: [LIST_GOALS],
    awaitRefetchQueries: true,
    onCompleted: ({deleteGoal}) => {
      if (!deleteGoal?.ok) {
        return enqueueSnackbar('Unable to delete goal.', {variant: 'error'});
      }
      return enqueueSnackbar('The selected goal has been deleted.', {variant: 'success'});
    },
    onError: (err) => {
      enqueueSnackbar(`Unable to delete goal. ${err.message}`, {
        variant: 'error',
        autoHideDuration: null,
      });
    },
  });
  const deleteFunc = (goalId: string) =>
    deleteGoalMutation({
      variables: {
        goalId,
      },
    }).then(() => {
      onClose();
    });

  if (loading) {
    return <Loading entityName="goal" />;
  }
  if (data?.goalDetails?.ovrecommends?.length) {
    return (
      <Box
        sx={{
          padding: 2,
          gap: 2,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'flex-start',
        }}
      >
        <Typography variant="h5">Delete Goal</Typography>
        <Typography>
          This goal has recommendations associated with it. Please first either a) delete
          the recommendations or b) remove the goal from each recommendation.
        </Typography>
        {data.goalDetails.ovrecommends.map((recommendation) => (
          <a
            key={recommendation.id}
            href={`./${createRecommendUrl}?ovrecommendId=${recommendation.id}`}
          >
            <Typography>{recommendation.name}</Typography>
          </a>
        ))}
        <Button
          variant="contained"
          onClick={() => {
            refetch();
          }}
          disabled={loading}
        >
          Refresh goal
        </Button>
      </Box>
    );
  }
  if (goal.isInCuro) {
    if (hasPermission(djangoUser, [GlobalPermissionEnum.can_access_curo_FE])) {
      return (
        <Box
          sx={{
            padding: 2,
            gap: 2,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'flex-start',
          }}
        >
          <Typography variant="h5">Delete Goal</Typography>
          <Typography>This goal is in CURO, please delete in CURO first.</Typography>
          <Button
            variant="contained"
            onClick={() => {
              refetch();
            }}
            disabled={loading}
          >
            Refresh goal
          </Button>
          <OpenInCuro variant="contained" curoType="goal" curoId={goal.curoId} alwaysShow>
            Open goal in curo
          </OpenInCuro>
        </Box>
      );
    }
    return (
      <Box
        sx={{
          padding: 2,
          gap: 2,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'flex-start',
        }}
      >
        <Typography variant="h5">Delete Goal</Typography>
        <Typography>Please contact a member of support to delete this goal.</Typography>
      </Box>
    );
  }
  return (
    <Box sx={{padding: 2}}>
      <h5>Delete Goal</h5>
      <p>Are you sure you want to delete this goal?</p>
      <Button
        variant="contained"
        onClick={() => {
          deleteFunc(goal.id);
        }}
        disabled={deleteLoading}
      >
        {deleteLoading ? 'Deleting...' : 'Delete'}
      </Button>
      <Button onClick={() => onClose()}>Cancel delete</Button>
    </Box>
  );
};
